import React from "react";
import PropTypes from "prop-types";
import PerfectScrollbar from "react-perfect-scrollbar";
import {
  Box,
  Card,
  CardActions,
  CardContent,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow
} from "@mui/material";
import { FormattedMessage } from "react-intl";
import { formatDate, formatRelativeTime } from "../../../../utils/formatter/dateTimeFormatter";
import { HasAccessTo } from "../../../../components/Authorization";
import { CONNECTION, CREDENTIAL } from "../../../../common/Authorization/entities";
import { EDIT, VIEW } from "../../../../common/Authorization/permissions";
import { TranslationKeys } from "../../../../translations";
import KebabMenu from "../../../../components/KebabMenu";
import { ConfirmDialog } from "../../../../components";
import { useDialog } from "../../../../hooks/useDialog";
const CredentialsTable = ({
  className,
  credentials,
  handleShowDetails,
  handlePageChange,
  handleRowsPerPageChange,
  handleClickChange,
  onDelete,
  page,
  rowsPerPage,
  totalCount,
  loading
}) => {
  const disabledIfNoAccessToCredentialsEdit = !HasAccessTo(CREDENTIAL, EDIT);
  const disabledIfNoAccessToConnectionsView = !HasAccessTo(CONNECTION, VIEW);
  const {
    dialogState: showDeleteConfirmationDialog,
    closeDialog: closeDeleteConfirmationDialog,
    openDialog: openDeleteConfirmationDialog
  } = useDialog();
  const [credentialIdToDelete, setCredentialIdToDelete] = React.useState(null);
  const onDeleteClick = React.useCallback(
    (credentialId) => () => {
      openDeleteConfirmationDialog();
      setCredentialIdToDelete(credentialId);
    },
    [openDeleteConfirmationDialog]
  );
  const onClickConfirmDelete = React.useCallback(() => {
    onDelete(credentialIdToDelete);
    setCredentialIdToDelete(null);
  }, [credentialIdToDelete, onDelete]);
  const buildRowActions = React.useCallback(
    (credential) => {
      const actions = [
        {
          element: /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.credentials_connections_show }),
          onClick: () => handleShowDetails(credential.credentialId),
          disabled: disabledIfNoAccessToConnectionsView
        },
        {
          element: /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.global_edit }),
          onClick: () => handleClickChange(credential),
          disabled: disabledIfNoAccessToCredentialsEdit
        },
        {
          element: /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.button_delete }),
          onClick: onDeleteClick(credential.credentialId),
          disabled: disabledIfNoAccessToCredentialsEdit
        }
      ];
      return /* @__PURE__ */ React.createElement(KebabMenu, { items: actions });
    },
    [
      disabledIfNoAccessToConnectionsView,
      disabledIfNoAccessToCredentialsEdit,
      handleClickChange,
      handleShowDetails,
      onDeleteClick
    ]
  );
  return /* @__PURE__ */ React.createElement(React.Fragment, null, showDeleteConfirmationDialog && !!credentialIdToDelete && /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      title: /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.global_delete }),
      open: showDeleteConfirmationDialog,
      onClose: closeDeleteConfirmationDialog,
      onConfirm: onClickConfirmDelete
    },
    /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.global_deleteConfirmationMessage })
  ), /* @__PURE__ */ React.createElement(Card, { className }, /* @__PURE__ */ React.createElement(CardContent, { sx: { padding: 0 } }, /* @__PURE__ */ React.createElement(PerfectScrollbar, null, /* @__PURE__ */ React.createElement(Box, { sx: { minWidth: 1050 } }, /* @__PURE__ */ React.createElement(Table, null, /* @__PURE__ */ React.createElement(TableHead, null, /* @__PURE__ */ React.createElement(TableRow, null, /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.credentials_name })), /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.owners_title })), /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.credentials_type })), /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.credentials_packageType })), /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.credentials_expires })), /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.credentials_createdAt })), /* @__PURE__ */ React.createElement(TableCell, null))), /* @__PURE__ */ React.createElement(TableBody, null, loading && new Array(rowsPerPage).fill((index) => index).map((value, index) => /* @__PURE__ */ React.createElement(TableRow, { key: index }, /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(Skeleton, null)), /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(Skeleton, null)), /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(Skeleton, null)), /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(Skeleton, null)), /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(Skeleton, null)), /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(Skeleton, null)), /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(Skeleton, null)))), !loading && credentials.map((credential) => /* @__PURE__ */ React.createElement(TableRow, { hover: true, key: credential.credentialId }, /* @__PURE__ */ React.createElement(TableCell, null, credential.name), /* @__PURE__ */ React.createElement(TableCell, null, credential.owner?.ownerName), /* @__PURE__ */ React.createElement(TableCell, null, credential["@type"]), /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: `packages.type.${credential.packageType}` })), /* @__PURE__ */ React.createElement(TableCell, null, formatRelativeTime(credential.accessTokenExpires)), /* @__PURE__ */ React.createElement(TableCell, null, formatDate(credential.createdAt)), /* @__PURE__ */ React.createElement(TableCell, null, buildRowActions(credential))))))))), /* @__PURE__ */ React.createElement(CardActions, { sx: { justifyContent: "flex-end" } }, /* @__PURE__ */ React.createElement(
    TablePagination,
    {
      component: "div",
      count: totalCount,
      onPageChange: handlePageChange,
      onRowsPerPageChange: handleRowsPerPageChange,
      page,
      rowsPerPage,
      rowsPerPageOptions: [5, 10, 25, 50, 100]
    }
  ))));
};
CredentialsTable.propTypes = {
  className: PropTypes.string,
  credentials: PropTypes.array.isRequired
};
export default CredentialsTable;
